import classNames from 'classnames';
import { useEffect } from 'react';
import type { GroupBase, SingleValue } from 'react-select';

import type { ChartType } from '@revelio/core';
import { Select as RevelioSelect, Text } from '@revelio/ui';

import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PrimaryFilterConfig } from '../PageConfigProvider/page-reducer';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import styles from './dimension-source-selector.module.css';
import labelStyles from './section-label.module.css';
import {
  type DimensionSourceOption,
  getAllDimensionSourceOptions,
} from './utils/dimension-source-options';

const getDimensionSourceLabel = (chartType: ChartType | undefined) => {
  if (chartType === 'bar' || chartType === 'stacked-bar') return 'Bar';
  return 'Comparison';
};

export const DimensionSourceSelector = ({
  item,
  primaryFilter,
}: {
  item: PositionedItem;
  primaryFilter: PrimaryFilterConfig;
}) => {
  const { dispatch } = useTabContentConfig();
  const primaryFilterId = primaryFilter.id;

  const { dimensionSource } = item?.plot ?? {};
  const metric = item?.plot?.defaultMetricOptions?.[0];

  const availableDimensionSources = getAllDimensionSourceOptions({
    chartType: item?.plot?.chartType,
    primaryFilterId,
    metric,
  });

  const matchedDimensionSource =
    availableDimensionSources.find(
      (source) => source.value === dimensionSource
    ) ?? null;

  // Always show a value: fall back to the `primary` option when nothing is set.
  const selectedDimensionSource =
    matchedDimensionSource ??
    availableDimensionSources.find((source) => source.value === 'primary') ??
    null;

  useEffect(() => {
    if (
      (!matchedDimensionSource?.value || !availableDimensionSources.length) &&
      !!dimensionSource &&
      metric !== undefined
    ) {
      dispatch({
        type: 'REMOVE_ITEM_DIMENSION_SOURCE',
        payload: { id: item.id },
      });
    }
  }, [
    dimensionSource,
    matchedDimensionSource?.value,
    availableDimensionSources?.length,
    metric,
    dispatch,
    item.id,
  ]);

  const onChangeDimensionSource = (
    option: SingleValue<DimensionSourceOption>
  ) => {
    if (!option) {
      dispatch({
        type: 'REMOVE_ITEM_DIMENSION_SOURCE',
        payload: { id: item.id },
      });
      return;
    }

    dispatch({
      type: 'UPDATE_ITEM_DIMENSION_SOURCE',
      payload: { id: item.id, dimensionSource: option.value },
    });
  };

  if (availableDimensionSources.length === 0) return null;

  const label = getDimensionSourceLabel(item?.plot?.chartType);

  return (
    <div className={styles.section}>
      <Text
        className={classNames(labelStyles.sectionLabel, styles.label)}
        fontSize="sm"
      >
        {label}
      </Text>
      <RevelioSelect<
        DimensionSourceOption,
        false,
        GroupBase<DimensionSourceOption>
      >
        options={availableDimensionSources}
        value={selectedDimensionSource}
        onChange={onChangeDimensionSource}
        isSearchable
        size="sm"
      />
    </div>
  );
};
